/*
 * RS256 JWT verification on the browser's built-in WebCrypto (window.crypto.subtle).
 *
 * jsonwebtoken 9.x verify() depends on Node-only crypto APIs (KeyObject / createPublicKey) and throws in the
 * browser, so signature verification is done here instead. jsonwebtoken is still used for decode() only.
 *
 * WebCrypto is only available in secure contexts (https, or http://localhost).
 */

//DER prefix for: SEQUENCE { OID rsaEncryption, NULL } - the AlgorithmIdentifier of an RSA SubjectPublicKeyInfo.
const RSA_ALGORITHM_IDENTIFIER = [0x30, 0x0d, 0x06, 0x09, 0x2a, 0x86, 0x48, 0x86, 0xf7, 0x0d, 0x01, 0x01, 0x01, 0x05, 0x00];

function base64ToBytes(b64) {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

function base64UrlToBytes(b64url) {
  let b64 = b64url.replace(/-/g, '+').replace(/_/g, '/');
  while (b64.length % 4) {
    b64 += '=';
  }
  return base64ToBytes(b64);
}

function derLength(length) {
  if (length < 128) {
    return [length];
  }
  const bytes = [];
  while (length > 0) {
    bytes.unshift(length & 0xff);
    length = length >> 8;
  }
  return [0x80 | bytes.length].concat(bytes);
}

//WebCrypto imports SubjectPublicKeyInfo (BEGIN PUBLIC KEY) only. rsaPublicKeyToPEM produces PKCS#1
//(BEGIN RSA PUBLIC KEY), so wrap that in the SubjectPublicKeyInfo structure.
function pkcs1ToSpki(pkcs1) {
  const bitString = [0x03].concat(derLength(pkcs1.length + 1), [0x00], Array.from(pkcs1));
  const body = RSA_ALGORITHM_IDENTIFIER.concat(bitString);
  return new Uint8Array([0x30].concat(derLength(body.length), body));
}

function pemToSpki(pem) {
  const match = /-----BEGIN (RSA PUBLIC KEY|PUBLIC KEY)-----([\s\S]+?)-----END \1-----/.exec(pem || '');
  if (!match) {
    throw new Error('invalid public key');
  }
  const der = base64ToBytes(match[2].replace(/\s+/g, ''));
  return match[1] === 'RSA PUBLIC KEY' ? pkcs1ToSpki(der) : der;
}

async function verify(token, publicKeyPem, options) {
  const parts = typeof token === 'string' ? token.split('.') : [];
  if (parts.length !== 3) {
    throw new Error('jwt malformed');
  }

  const decoder = new TextDecoder();
  let header, payload;
  try {
    header = JSON.parse(decoder.decode(base64UrlToBytes(parts[0])));
    payload = JSON.parse(decoder.decode(base64UrlToBytes(parts[1])));
  } catch (err) {
    throw new Error('jwt malformed');
  }

  //Only RS256 is accepted - never trust the token to pick its own algorithm (e.g. "none" / HS256).
  if (!header || header.alg !== 'RS256') {
    throw new Error('invalid algorithm');
  }

  const key = await window.crypto.subtle.importKey('spki', pemToSpki(publicKeyPem), { name: 'RSASSA-PKCS1-v1_5', hash: 'SHA-256' }, false, ['verify']);
  const signedData = new TextEncoder().encode(parts[0] + '.' + parts[1]);
  const valid = await window.crypto.subtle.verify('RSASSA-PKCS1-v1_5', key, base64UrlToBytes(parts[2]), signedData);
  if (!valid) {
    throw new Error('invalid signature');
  }

  //Expiry is mandatory. clockTolerance is the number of seconds of clock skew tolerated.
  const clockTolerance = options.clockTolerance || 0;
  if (typeof payload.exp !== 'number') {
    throw new Error('invalid exp value');
  }
  if (Math.floor(Date.now() / 1000) >= payload.exp + clockTolerance) {
    throw new Error('jwt expired');
  }

  if (options.issuer) {
    const issuers = Array.isArray(options.issuer) ? options.issuer : [options.issuer];
    if (issuers.indexOf(payload.iss) === -1) {
      throw new Error('jwt issuer invalid. expected: ' + issuers.join(', '));
    }
  }

  return payload;
}

/*
 * Verifies an RS256 signed JWT and calls back with (err, decodedPayload), mirroring jsonwebtoken's verify().
 *   options.clockTolerance - seconds of clock skew tolerated when checking exp
 *   options.issuer         - expected issuer, or array of accepted issuers (optional)
 * The nbf claim is not checked (identity servers' clocks differ).
 */
export function verifyJwt(token, publicKeyPem, options, callback) {
  verify(token, publicKeyPem, options || {}).then(
    (decoded) => callback(null, decoded),
    (err) => callback(err)
  );
}
